/*
 * Miniami commerce runtime.
 * One re-entrant, section-scoped controller for bundle selection, cart upsells,
 * and the mobile checkout affordance. Cart mutations occur only after an
 * explicit user action; no page-load cart/customer data is changed.
 */
(() => {
  'use strict';

  if (window.__miniamiParallelCommerceRuntime) return;
  window.__miniamiParallelCommerceRuntime = true;

  const bundleControllers = new WeakMap();
  const upsellControllers = new WeakMap();
  const pillControllers = new WeakMap();
  const cartRootSelector = '[data-miniami-parallel-cart], [data-miniami-parallel-cart-drawer]';

  const textKey = Object.freeze({
    add: 'miniamiParallelAdd',
    remove: 'miniamiParallelRemove',
    added: 'miniamiParallelAdded',
    adding: 'miniamiParallelAdding',
    retry: 'miniamiParallelRetry',
    cartError: 'miniamiParallelCartError',
    drawerError: 'miniamiParallelDrawerError',
    viewCart: 'miniamiParallelViewCart',
    backendPending: 'miniamiParallelBackendPending',
    selected: 'miniamiParallelSelected',
    picked: 'miniamiParallelPicked',
    alreadyInCart: 'miniamiParallelAlreadyInCart',
    unavailableParent: 'miniamiParallelUnavailableParent',
    requiredParent: 'miniamiParallelRequiredParent',
    regularPrice: 'miniamiParallelRegularPrice',
    bundlePrice: 'miniamiParallelBundlePrice',
    excludedNote: 'miniamiParallelExcludedNote',
    offerNoteEstimated: 'miniamiParallelOfferNoteEstimated',
    offerNoteFallback: 'miniamiParallelOfferNoteFallback',
    offerUnavailable: 'miniamiParallelOfferUnavailable',
  });
  const defaultText = Object.freeze({
    add: 'Add to cart',
    remove: 'Remove',
    added: 'Added',
    adding: 'Adding',
    retry: 'Try again',
    cartError: 'There was an error while updating your cart. Please try again.',
    drawerError: 'Added to your cart, but the drawer could not open.',
    viewCart: 'View cart',
    backendPending: 'Pricing details are not configured yet.',
    selected: 'selected',
    picked: 'picked',
    alreadyInCart: 'Already in cart',
    unavailableParent: 'The required pattern is unavailable.',
    requiredParent: 'Required parent pattern',
    regularPrice: 'Regular price',
    bundlePrice: 'Sale price',
    // F4 (t_32351e80): the last customer-facing English literals in this file.
    // Merchant-editable via the section's data-miniami-parallel-* hooks, like
    // every other label; these values stay the source-consistent fallbacks.
    excludedNote: 'Not included in the bundle offer — added at its own price.',
    offerNoteEstimated: 'Discount applies automatically at checkout.',
    offerNoteFallback: 'The bundle offer could not be loaded. Individual pattern prices apply.',
    offerUnavailable: 'Offer unavailable',
  });

  const all = (root, selector) => [...root.querySelectorAll(selector)];
  const rootsFor = (scope, selector) => {
    if (scope?.matches?.(selector)) return [scope, ...all(scope, selector)];
    return all(scope || document, selector);
  };

  const textFor = (root, key) => {
    const attr = textKey[key];
    return root?.dataset?.[attr] || document.documentElement.dataset?.[attr] || defaultText[key] || key;
  };

  const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  })[character]);

  const formatMoney = (cents, currency) => {
    const amount = Number.isFinite(Number(cents)) ? Number(cents) : 0;
    if (window.Shopify?.formatMoney && window.Shopify.money_format) {
      return window.Shopify.formatMoney(amount, window.Shopify.money_format);
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(amount / 100);
    } catch (error) {
      return `${(amount / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  };

  const route = (name, fallback) => window.routes?.[name] || fallback;

  const notifyCartUpdate = (cart, payload, source) => {
    document.dispatchEvent(new CustomEvent('miniami:parallel-cart-updated', { detail: { cart, payload } }));
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      return publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData: cart, payload }).catch(() => undefined);
    }
    return Promise.resolve();
  };

  // The add response's sections can precede AOV's cart transformation. Read the
  // section after the verified cart readback, then let Dawn replace the whole
  // drawer (including its conditional empty/header markup) and manage focus.
  const openBundleCartDrawer = async (trigger, addedState = {}) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.renderContents !== 'function') throw new Error('cart drawer unavailable');
    const response = await fetch(`${route('cart_url', '/cart')}?sections=cart-drawer,cart-icon-bubble`);
    if (!response.ok) throw new Error('cart drawer refresh failed');
    const sections = await response.json();
    const html = sections['cart-drawer'];
    const refreshed = new DOMParser().parseFromString(html || '', 'text/html').querySelector('#CartDrawer');
    if (!refreshed?.querySelector('[data-cart-heading]')
      || refreshed.querySelector('.drawer__inner-empty')
      || !refreshed.querySelector('.cart-item')
      || !sections['cart-icon-bubble']) throw new Error('cart drawer returned stale sections');
    drawer.setActiveElement(trigger);
    drawer.classList.remove('is-empty');
    drawer.renderContents({ ...addedState, sections, announceAdded: true });
    if (typeof blockMemberCheckout === 'function') blockMemberCheckout(typeof commercePending !== 'undefined' && commercePending > 0);
  };

  let commercePending = 0, commerceQueue = Promise.resolve();
  const enqueueCommerce = (work) => {
    commercePending += 1;
    blockMemberCheckout(true);
    document.dispatchEvent(new CustomEvent('miniami:cart-mutation-start'));
    const task = commerceQueue.catch(() => undefined).then(work);
    commerceQueue = task.finally(() => {
      commercePending -= 1;
      if (!commercePending && !memberOperationPending && !document.querySelector('[data-cart-sync-unconfirmed]')) blockMemberCheckout(false);
    });
    // Caller observes task rejection; the tail must not be an unhandled promise.
    commerceQueue.catch(() => undefined);
    return task;
  };
  const pendingDrawer = (trigger) => {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return () => {};
    drawer.clearAddedState?.();
    drawer.querySelectorAll('[data-commerce-error]').forEach(node=>node.remove());
    const message = document.createElement('p');
    message.dataset.commercePending = ''; message.setAttribute('role', 'status');
    message.textContent = 'Updating cart… Totals will refresh when confirmed.';
    (drawer.querySelector('.drawer__inner') || drawer).prepend(message);
    drawer.open?.(trigger);
    return () => message.remove();
  };

  const parseTiers = (root) => {
    const thresholds = (root.dataset.tierItems || '2,3,4,5,6')
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value) => Number.isFinite(value));
    const percentages = (root.dataset.tierPercents || '10,15,20,25,30')
      .split(',')
      .map((value) => Number.parseInt(value.trim(), 10))
      .filter((value) => Number.isFinite(value));
    return thresholds.map((min, index) => ({ min, pct: percentages[index] || 0 }));
  };

  const tierFor = (tiers, count) => tiers.reduce(
    (current, tier) => (count >= tier.min && tier.min >= current.min ? tier : current),
    { min: 0, pct: 0 },
  );

  // Native AOV cart-transform contract, verified with the existing offer.
  // No client-supplied prices: the provider owns prices and component expansion.
  const parseAovConfig = (value) => {
    try {
      const data = typeof value === 'string' ? JSON.parse(value) : value;
      if (!data || !Number.isSafeInteger(Number(data.variantId))) return null;
      const offers = typeof data.offers === 'string' ? JSON.parse(data.offers) : data.offers;
      const offer = Array.isArray(offers) && offers.find((entry) => entry.type === 'bundle-builder'
        && entry.status === 'active' && entry.enabledDiscount === true
        && entry.bundleBuilderId === `gid://shopify/ProductVariant/${data.variantId}`);
      const discount = data.discount;
      if (!offer || !discount?.enabledDiscount || discount.discountType !== 'percentage'
        || !data.eligible || typeof data.eligible !== 'object') return null;
      const tiers = discount.discountOptions.map((entry) => ({ min: Number(entry.quantity), pct: Number(entry.discountValue) }));
      if (!tiers.length || tiers.some((entry) => !Number.isInteger(entry.min) || entry.min < 2
        || !Number.isFinite(entry.pct) || entry.pct < 0 || entry.pct > 100)) return null;
      return { variantId: Number(data.variantId), offerId: offer.id, eligible: data.eligible, tiers };
    } catch (error) { return null; }
  };

  const planAovBundle = (config, items) => {
    const unique = [...new Map(items.map((item) => [String(item.id), item])).values()];
    const eligible = config ? unique.filter((item) => Object.prototype.hasOwnProperty.call(config.eligible, String(item.id))) : [];
    const tier = tierFor(config?.tiers || [], eligible.length);
    const bundled = tier.min > 0 ? eligible : [];
    const subtotal = bundled.reduce((sum, item) => sum + item.price, 0);
    const lines = [];
    if (bundled.length) lines.push({
      id: config.variantId, quantity: 1,
      properties: {
        _aov_offerId: config.offerId,
        _aov_offerType: 'bundle-builder',
        _aov_bundleConfiguration: JSON.stringify([...bundled].sort((a, b) => a.id - b.id).map((item) => ({ i: String(item.id) }))),
      },
    });
    unique.filter((item) => !bundled.includes(item)).forEach((item) => lines.push({ id: item.id, quantity: 1, properties: item.properties }));
    return { lines, eligibleCount: eligible.length, subtotal, tier, discount: subtotal - Math.round(subtotal * (100 - tier.pct) / 100) };
  };

  async function readCart({ signal } = {}) {
    const response = await fetch(`${route('cart_url', '/cart')}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    });
    if (!response.ok) throw new Error('cart read failed');
    return response.json();
  }

  // URLs carry ONE native line key, never selections/prices. Resolve it against
  // this browser's first-party cart and the active provider, all-or-nothing.
  const bundleSelectionIds = (value) => {
    try {
      const entries = JSON.parse(value);
      if (!Array.isArray(entries) || entries.length < 2 || entries.length > 40) return null;
      const ids = entries.map((entry) => {
        if (!entry || typeof entry.i !== 'string' || !/^[1-9][0-9]*$/.test(entry.i)
          || !Number.isSafeInteger(Number(entry.i))
          || (entry.q !== undefined && entry.q !== 1 && entry.q !== '1')
          || Object.keys(entry).some((key) => key !== 'i' && key !== 'q')) throw new Error('invalid selection');
        return entry.i;
      });
      return new Set(ids).size === ids.length ? ids.sort() : null;
    } catch (error) { return null; }
  };
  const resolveBundleContinuation = (cart, key, config) => {
    if (!config?.eligible || !Array.isArray(cart?.items)
      || typeof key !== 'string' || !/^[1-9][0-9]*:[a-f0-9]{32}$/.test(key)) return null;
    const matches = cart.items.filter((line) => line?.key === key);
    const line = matches.length === 1 ? matches[0] : null;
    if (!line || String(line.variant_id) !== String(config.variantId) || line.quantity !== 1
      || line.has_components !== true || line.selling_plan_allocation
      || line.properties?._aov_offerType !== 'bundle-builder'
      || line.properties?._aov_offerId !== config.offerId) return null;
    const ids = bundleSelectionIds(line.properties._aov_bundleConfiguration);
    if (!ids || ids.some((id) => !Object.prototype.hasOwnProperty.call(config.eligible, id))) return null;
    return { key, ids, signature: stableCartValue([key, config.offerId, ids, line.quantity, line.properties]) };
  };

  // Shopify's native AOV transform can finish after /cart/add.js resolves. A
  // larger 6+ selection is the slowest path, so one immediate /cart.js read can
  // falsely look empty even though the add is still being expanded. Poll only
  // for the exact plan we just submitted, with a short bounded backoff; this is
  // recovery from provider latency, not an unbounded retry loop.
  const readCartUntil = async (predicate, initialCart = null) => {
    let cart = initialCart || await readCart();
    const delays = [80, 140, 220, 320, 460, 620, 820];
    for (const delay of delays) {
      if (predicate(cart)) return cart;
      await new Promise((resolve) => setTimeout(resolve, delay));
      cart = await readCart();
    }
    return cart;
  };

  // C70: one server-side parent update for any valid replacement selection.
  // No add/remove loop, replacement carrier, client prices or POST retries.
  // Native qualification of larger/bulk edits remains a separate release gate.
  const stableCartValue = (value) => JSON.stringify(value, function (key, entry) {
    if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
      return Object.fromEntries(Object.keys(entry).sort().map((name) => [name, entry[name]]));
    }
    return entry;
  });
  const bundlePeers = (cart, key) => stableCartValue([cart.token, cart.currency, cart.note, cart.attributes,
    cart.cart_level_discount_applications, cart.discount_codes,
    cart.items.filter((line) => line.key !== key).slice().sort((a, b) => a.key.localeCompare(b.key))]);
  const validateNativeBundleRoster = (line, ids, native, currency) => {
    const parts = native?.components;
    if (!line || line.has_components !== true || line.quantity !== 1 || line.selling_plan_allocation
      || !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0
      || native?.key !== line.key || typeof native.currency !== 'string'
      || (currency !== undefined && native.currency !== currency)
      || !Array.isArray(parts) || parts.length !== ids.length
      || new Set(ids).size !== ids.length
      || parts.some((part) => !ids.includes(part.variant_id) || part.quantity !== 1
        || part.requires_shipping !== false || part.gift_card !== false || part.selling_plan !== false
        || part.role !== 'standalone_pattern' || part.available !== true
        || !Number.isSafeInteger(part.regular_unit_price) || part.regular_unit_price < 0
        || !Number.isSafeInteger(part.final_line_price) || part.final_line_price < 0
        || part.final_line_price > part.regular_unit_price)
      || new Set(parts.map((part) => part.variant_id)).size !== parts.length
      || parts.reduce((sum, part) => sum + part.final_line_price, 0) !== line.final_line_price) {
      throw new Error('Native pattern identities or prices could not be verified.');
    }
    return native;
  };
  const verifyNativeBundle = async (line, ids, currency) => {
    const response = await fetch(`${route('cart_url', '/cart')}?sections=cart-drawer`, { cache: 'no-store' });
    if (!response.ok) throw new Error('native components read failed');
    const sections = await response.json();
    const doc = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
    const rows = [...doc.querySelectorAll('.cart-item[data-line-key]')].filter((row) => row.dataset.lineKey === line.key);
    const roster = rows.length === 1 && rows[0].querySelector('[data-bundle-roster]');
    const actual = roster ? [...roster.querySelectorAll('[data-bundle-selection]')].map((row) => row.dataset.selectionVariantId).sort() : [];
    if (!roster || roster.dataset.selectionSource !== 'native-components'
      || JSON.stringify(actual) !== JSON.stringify(ids)) throw new Error('native components not verified');
    return validateNativeBundleRoster(line, ids,
      JSON.parse(roster.querySelector('[data-cart-native-members]')?.textContent || 'null'), currency);
  };
  // Removing an expanded group always targets its current PARENT key. Child
  // remove URLs can delete the whole group and must never be guessed/replayed.
  const removeBundleContinuation = async (before, restored, config, io = {}) => {
    const current = resolveBundleContinuation(before, restored?.key, config);
    const original = before?.items?.find((line) => line.key === current?.key);
    if (!current || current.signature !== restored.signature || original.instructions?.can_remove === false) {
      throw new Error('This bundle changed or cannot be removed. Refresh your cart.');
    }
    return removeExactCartLine(before, current.key, io);
  };
  const removeExactCartLine = async (before, key, io = {}) => {
    const current = { key };
    const original = before?.items?.find((line) => line.key === key);
    if (!original || original.instructions?.can_remove === false) throw new Error('This cart line changed. Refresh your cart.');
    const read = io.read || readCart;
    const fresh = await read();
    if (stableCartValue(fresh) !== stableCartValue(before)) throw new Error('Your cart changed. Refresh it before trying again.');
    const change = io.change || (async (body) => {
      const response = await fetch(route('cart_change_url', `${route('cart_url', '/cart')}/change.js`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('native removal response failed');
    });
    const removed = (cart) => Array.isArray(cart?.items) && !cart.items.some((line) => line.key === current.key)
      && cart.items.length === before.items.length - 1
      && bundlePeers(cart, current.key) === bundlePeers(before, current.key)
      && cart.total_price === cart.items.reduce((sum, line) => sum + line.final_line_price, 0)
      && cart.item_count === cart.items.reduce((sum, line) => sum + line.quantity, 0);
    const uncertain = () => Object.assign(new Error('The removal could not be confirmed. Reload your cart; no request was repeated.'), { bundleRecoveryRequired: true });
    try { await change({ id: current.key, quantity: 0 }); } catch (error) { /* Reconcile even a lost ACK; never repeat POST. */ }
    let after;
    try {
      after = await read();
      for (const delay of [80, 140, 220, 320, 460, 620, 820]) {
        if (removed(after)) break;
        await (io.wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms))))(delay);
        after = await read();
      }
      if (!removed(after)) {
        if (stableCartValue(after) === stableCartValue(before)) throw new Error('The bundle was not removed. Your cart is unchanged. Please try again.');
        throw uncertain();
      }
      const confirmed = await read();
      if (!removed(confirmed) || stableCartValue(confirmed) !== stableCartValue(after)) throw uncertain();
      return { cart: confirmed, continuation: null, removed: true };
    } catch (error) {
      if (error.message === 'The bundle was not removed. Your cart is unchanged. Please try again.') throw error;
      throw uncertain();
    }
  };
  const updateBundleContinuation = async (before, restored, config, items, io = {}) => {
    if (items.length === 0) return removeBundleContinuation(before, restored, config, io);
    const read = async () => {
      const cart = await (io.read || readCart)();
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response.');
      return cart;
    };
    const verify = io.verify || verifyNativeBundle;
    const wait = io.wait || ((delay) => new Promise((resolve) => setTimeout(resolve, delay)));
    const change = io.change || (async (body) => {
      const response = await fetch(route('cart_change_url', `${route('cart_url', '/cart')}/change.js`), {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('native update response failed');
    });
    const current = resolveBundleContinuation(before, restored?.key, config);
    if (!current || current.signature !== restored.signature) throw new Error('This bundle changed. Return to your cart and choose Add more again.');
    const ids = items.map((item) => String(item.id)).sort();
    if (JSON.stringify(ids) === JSON.stringify(current.ids)) return { cart: before, continuation: current, already: true };
    if (ids.length < 2 || ids.length > 40 || new Set(ids).size !== ids.length
      || items.some((item) => !Object.prototype.hasOwnProperty.call(config.eligible, String(item.id))
        || !Number.isSafeInteger(item.id) || !Number.isSafeInteger(item.price) || item.price < 0)) {
      throw new Error('Choose 2 or more available patterns for this bundle. Your original bundle is unchanged.');
    }
    const plan = planAovBundle(config, items);
    if (plan.lines.length !== 1 || plan.lines[0].id !== config.variantId) throw new Error('Native offer unavailable. Your original bundle is unchanged.');
    const original = before.items.find((line) => line.key === current.key);
    if (original.instructions?.can_remove === false || original.instructions?.can_update_quantity === false) {
      throw new Error('This bundle cannot be edited. Your cart is unchanged.');
    }
    const properties = { ...original.properties, ...plan.lines[0].properties };
    const matches = (cart, expected) => cart.items.filter((line) => String(line.variant_id) === String(config.variantId)
      && line.properties?._aov_offerId === config.offerId && line.properties?._aov_offerType === 'bundle-builder'
      && JSON.stringify(bundleSelectionIds(line.properties?._aov_bundleConfiguration)) === JSON.stringify(expected));
    const ownedNew = (cart) => {
      const lines = matches(cart, ids);
      const line = lines.length === 1 ? lines[0] : null;
      return line && line.quantity === 1 && !line.selling_plan_allocation
        && stableCartValue(line.properties) === stableCartValue(properties)
        && matches(cart, current.ids).length === 0
        && bundlePeers(cart, line.key) === bundlePeers(before, original.key) ? line : null;
    };
    // No Shopify CAS exists: re-read immediately before POST; a stale key fails
    // closed. Cross-tab changes after this read remain an explicit limitation.
    const fresh = await read();
    if (matches(fresh, ids).length) throw new Error('That selection already belongs to another cart group. Your original bundle is unchanged.');
    const freshCurrent = resolveBundleContinuation(fresh, current.key, config);
    if (!freshCurrent || freshCurrent.signature !== current.signature
      || JSON.stringify(fresh.items) !== JSON.stringify(before.items)
      || bundlePeers(fresh, current.key) !== bundlePeers(before, current.key)) throw new Error('Your cart changed. Return to the cart before trying again.');
    const uncertain = (message) => Object.assign(new Error(message), { bundleRecoveryRequired: true });
    try { await change({ id: current.key, quantity: 1, properties }); } catch (error) { /* GET reconciliation only; never repeat an uncertain POST. */ }
    let after;
    try { after = await read(); } catch (error) {
      throw uncertain('The update response could not be confirmed. Check your cart; no request was repeated.');
    }
    // Read-only reconciliation, never mutation retry. The opaque key can change.
    for (const delay of [80, 140, 220, 320, 460, 620, 820]) {
      if (ownedNew(after)?.has_components === true) break;
      await wait(delay);
      try { after = await read(); } catch (error) {
        throw uncertain('The update response could not be confirmed. Check your cart; no request was repeated.');
      }
    }
    const updated = ownedNew(after);
    if (!updated) {
      if (stableCartValue(after.items) === stableCartValue(before.items)
        && bundlePeers(after, current.key) === bundlePeers(before, current.key)) throw new Error('The update was not applied. Your original bundle is unchanged.');
      throw uncertain('The update result is uncertain. Check your cart before trying again; no request was repeated.');
    }
    try {
      if (updated.has_components !== true || !Number.isSafeInteger(updated.final_line_price)) throw new Error('unexpanded native group');
      await verify(updated, ids, before.currency);
      const confirmed = await read();
      const confirmedLine = ownedNew(confirmed);
      if (!confirmedLine || JSON.stringify(confirmedLine) !== JSON.stringify(updated)) throw new Error('cart changed during verification');
      return { cart: confirmed, continuation: resolveBundleContinuation(confirmed, confirmedLine.key, config), already: false };
    } catch (error) {
      // Roll back only the uniquely identified configuration this operation
      // wrote, and only if an independent read still has exactly that state.
      try {
      const recovery = await read();
      const recoveryLine = ownedNew(recovery);
      if (!recoveryLine || stableCartValue(recoveryLine) !== stableCartValue(updated)) throw uncertain('Your cart changed during recovery. Check it before trying again.');
      try { await change({ id: recoveryLine.key, quantity: 1, properties: original.properties }); } catch (rollbackError) { /* An uncertain rollback is reconciled by GET, never replayed. */ }
      let rolledBack = await read();
      for (const delay of [80, 140, 220, 320, 460, 620, 820]) {
        if (matches(rolledBack, current.ids).length === 1) break;
        await wait(delay); rolledBack = await read();
      }
      const restoredLines = matches(rolledBack, current.ids);
      const old = restoredLines.length === 1 ? restoredLines[0] : null;
      if (!old || old.quantity !== 1 || old.has_components !== true
        || old.original_price !== original.original_price || old.final_line_price !== original.final_line_price
        || stableCartValue(Object.fromEntries(Object.entries(old).filter(([key]) => key !== 'key')))
          !== stableCartValue(Object.fromEntries(Object.entries(original).filter(([key]) => key !== 'key')))
        || bundlePeers(rolledBack, old.key) !== bundlePeers(before, original.key)) throw uncertain('Recovery could not be verified. Check your cart before trying again.');
      await verify(old, current.ids, before.currency);
      const confirmedRollback = await read();
      if (stableCartValue(confirmedRollback) !== stableCartValue(rolledBack)) throw uncertain('Your cart changed during recovery. Check it before trying again.');
      // Rollback can also change Shopify's opaque key. Bind the exact verified
      // restored state so a deliberate retry edits it instead of a spent key.
      throw Object.assign(new Error(`The new selection could not be verified. Your original ${current.ids.length}-pattern bundle was restored.`), {
        restoredBundle: { cart: confirmedRollback, continuation: resolveBundleContinuation(confirmedRollback, old.key, config) },
      });
      } catch (recoveryError) {
        if (recoveryError.restoredBundle) throw recoveryError;
        throw uncertain('Recovery could not be verified. Check your cart before trying again; no request was repeated.');
      }
    }
  };

  // Cart-row adapter: native child keys are NEVER mutation targets.
  const planBundleMemberRemoval = (cart, intent, config, native) => {
    const fail = () => { throw new Error('This selection changed or its native prices are unavailable. Refresh your cart.'); };
    if (!intent || typeof intent.memberId !== 'string' || !/^[1-9][0-9]*$/.test(intent.memberId)
      || !Number.isSafeInteger(Number(intent.memberId)) || intent.offerId !== config?.offerId) fail();
    const current = resolveBundleContinuation(cart, intent.key, config);
    if (!current || !Array.isArray(intent.ids)
      || JSON.stringify([...intent.ids].sort()) !== JSON.stringify(current.ids)) fail();
    const line = cart.items.find((item) => item.key === current.key);
    if (line.instructions?.can_remove === false || line.instructions?.can_update_quantity === false) fail();
    // Validate the complete native commercial roster BEFORE any removal POST,
    // including multi-member removals, not only the terminal bridge.
    validateNativeBundleRoster(line, current.ids, native, cart.currency);
    const parts = native?.components;
    if (native?.key !== current.key || !Array.isArray(parts) || parts.length !== current.ids.length
      || !Number.isSafeInteger(line.final_line_price) || line.final_line_price < 0) fail();
    if (parts.some((part) => typeof part.variant_id !== 'string' || part.quantity !== 1
      || !current.ids.includes(part.variant_id) || !Number.isSafeInteger(part.final_line_price) || part.final_line_price < 0)
      || new Set(parts.map((part) => part.variant_id)).size !== parts.length
      || parts.reduce((sum, part) => sum + part.final_line_price, 0) !== line.final_line_price
      || parts.filter((part) => part.variant_id === intent.memberId).length !== 1) fail();
    const ids = current.ids.filter((id) => id !== intent.memberId);
    const properties = { ...line.properties, _aov_bundleConfiguration: JSON.stringify(ids.map((i) => ({ i }))) };
    if (ids.length === 1) {
      // Atomic parent0/new ordinary1; unrelated ordinary peers are read-only.
      // Never coalesce a same-variant peer or transfer merchant properties.
      const peers = cart.items.filter((item) => item.key !== current.key);
      const collision = peers.some((item) => String(item.variant_id) === ids[0]);
      const custom = Object.keys(line.properties).some((key) => !['_aov_offerId', '_aov_offerType', '_aov_bundleConfiguration'].includes(key));
      const survivor = parts.find((part) => part.variant_id === ids[0]);
      const quote = survivor?.regular_unit_price;
      const qualified = !collision && !custom
        && typeof cart.currency === 'string' && native.currency === cart.currency
        && Number.isSafeInteger(quote) && quote > 0 && survivor.available === true
        && parts.every((part) => part.requires_shipping === false && part.gift_card === false
          && part.role === 'standalone_pattern' && part.selling_plan === false);
      if (!qualified) return { kind: 'unqualified-dissolution', reason: collision ? 'ordinary-survivor-collision' : custom ? 'merchant-properties-ambiguous' : 'regular-native-quote-unavailable', survivorId: ids[0], key: current.key };
      return { kind: 'ordinary-dissolution', key: current.key, current, survivorId: ids[0], regularUnitPrice: quote,
        updates: { [current.key]: 0, [ids[0]]: 1 } };
    }
    // Sentinel zero satisfies existing seam's item shape, NOT a charged price.
    // The seam submits IDs/properties ONLY; Shopify supplies every final cent.
    return { kind: 'parent-selection', key: current.key, current, properties, items: ids.map((id) => ({ id: Number(id), price: 0 })) };
  };

  // Atomic bridge with exact peer preservation. Offline-qualified successor;
  // live mixed-cart acceptance still requires a newly authorised native test.
  const updateBundleDissolution = async (before, intent, config, native, io = {}) => {
    const read = io.read || readCart;
    const readNative = io.native || nativeRosterFromSections;
    const plan = planBundleMemberRemoval(before, intent, config, native);
    if (plan.kind !== 'ordinary-dissolution') throw new Error('This final-pattern change is unavailable. Use Edit bundle; your cart is unchanged.');
    const fresh = await read();
    if (stableCartValue(fresh) !== stableCartValue(before)) throw new Error('Your cart changed. Refresh it before trying again.');
    const quote = await readNative(plan.key);
    const confirmedPlan = planBundleMemberRemoval(fresh, intent, config, quote);
    if (confirmedPlan.kind !== 'ordinary-dissolution' || confirmedPlan.regularUnitPrice !== plan.regularUnitPrice
      || JSON.stringify(quote) !== JSON.stringify(native)) throw new Error('The native quote changed. Your cart is unchanged.');
    const update = io.update || (async (body) => {
      const response = await fetch(`${route('cart_url', '/cart')}/update.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('native dissolution response failed');
    });
    const ordinary = (cart) => {
      const lines = cart.items?.filter((item) => String(item.variant_id) === plan.survivorId);
      const line = lines?.length === 1 && lines[0];
      return line && line.key !== plan.key
        && line.quantity === 1 && line.has_components !== true && !line.item_components?.length
        && !line.selling_plan_allocation && Object.keys(line.properties || {}).length === 0
        && line.final_price === plan.regularUnitPrice && line.final_line_price === plan.regularUnitPrice
        && line.original_price === plan.regularUnitPrice && line.original_line_price === plan.regularUnitPrice
        && (!line.line_level_discount_allocations?.length) && (!line.discounts?.length)
        && bundlePeers(cart, line.key) === bundlePeers(before, plan.key)
        && cart.items.length === before.items.length
        && cart.total_price === cart.items.reduce((sum, item) => sum + item.final_line_price, 0)
        && cart.item_count === cart.items.reduce((sum, item) => sum + item.quantity, 0) ? line : null;
    };
    const uncertain = () => Object.assign(new Error('Could not verify the ordinary pattern. Check your cart; no request was repeated.'), { bundleRecoveryRequired: true });
    try { await update({ updates: plan.updates }); } catch (error) { /* Reconcile by GET only; never replay. */ }
    let after;
    try {
      after = await read();
      for (const delay of [80, 140, 220, 320, 460, 620, 820]) {
        if (ordinary(after)) break;
        await (io.wait || ((ms) => new Promise((resolve) => setTimeout(resolve, ms))))(delay); after = await read();
      }
      if (!ordinary(after)) {
        if (stableCartValue(after) === stableCartValue(before)) throw new Error('The update was not applied. Your original bundle is unchanged.');
        throw uncertain();
      }
    } catch (error) {
      if (error.message === 'The update was not applied. Your original bundle is unchanged.') throw error;
      throw uncertain();
    }
    let confirmed;
    try { confirmed = await read(); } catch (error) { throw uncertain(); }
    if (!ordinary(confirmed) || stableCartValue(confirmed) !== stableCartValue(after)) throw uncertain();
    return { cart: confirmed, continuation: null, survivorKey: ordinary(confirmed).key };
  };

  let memberOperationPending = false;
  let memberDisplayUnconfirmed = false;
  const memberSurfaceSelector = `${cartRootSelector}, .miniami-parallel-cart-footer`;
  const blockMemberCheckout = (blocked) => {
    memberDisplayUnconfirmed = blocked;
    all(document, memberSurfaceSelector).forEach((surface) => {
      surface.querySelectorAll('button[name="checkout"]').forEach((button) => {
        if (blocked && !button.disabled) {
          button.disabled = true;
          button.dataset.memberSyncBlocked = 'true';
        } else if (!blocked && button.dataset.memberSyncBlocked === 'true') {
          button.disabled = surface.classList.contains('is-empty');
          delete button.dataset.memberSyncBlocked;
        }
      });
      surface.querySelectorAll('.cart__dynamic-checkout-buttons').forEach((element) => {
        if (blocked && !element.hidden) {
          element.hidden = true;
          element.dataset.memberSyncBlocked = 'true';
        } else if (!blocked && element.dataset.memberSyncBlocked === 'true') {
          element.hidden = false;
          delete element.dataset.memberSyncBlocked;
        }
      });
    });
  };
  const nativeRosterFromSections = async (key) => {
    const response = await fetch(`${route('cart_url', '/cart')}?sections=cart-drawer`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Could not read current native pattern prices.');
    const sections = await response.json();
    const doc = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
    const row = [...doc.querySelectorAll('.cart-item[data-line-key]')].filter((node) => node.dataset.lineKey === key);
    const roster = row.length === 1 && row[0].querySelector('[data-bundle-roster][data-components-priced="true"]');
    if (!roster) throw new Error('Current native pattern details are incomplete. Refresh your cart.');
    return JSON.parse(roster.querySelector('[data-cart-native-members]').textContent);
  };
  const refreshMemberSections = async (expectedCart = null) => {
    expectedCart ||= await readCart();
    const pageCart = document.querySelector('cart-items[data-miniami-parallel-cart]');
    const descriptors = pageCart?.getSectionsToRender?.() || [];
    const ids = [...new Set(['cart-drawer', 'cart-icon-bubble', ...descriptors.map((part) => part.section)])];
    const response = await fetch(`${route('cart_url', '/cart')}?sections=${ids.join(',')}`, { cache: 'no-store' });
    if (!response.ok) throw new Error('Cart updated, but its display could not refresh. Reload your cart before another change.');
    const sections = await response.json();
    const parse = (html) => new DOMParser().parseFromString(html || '', 'text/html');
    const drawer = document.querySelector('#CartDrawer');
    const freshDrawer = parse(sections['cart-drawer']).querySelector('#CartDrawer');
    if (drawer && !freshDrawer) throw new Error('Cart display incomplete. Reload your cart.');
    const validateRows = (source) => {
      const rows = [...source.querySelectorAll('.cart-item[data-line-key]:not(.cart-item__nested-line)')];
      const keys = rows.map((row) => row.dataset.lineKey).sort();
      if (JSON.stringify(keys) !== JSON.stringify(expectedCart.items.map((line) => line.key).sort())) {
        throw new Error('Cart changed, but its display is stale. Reload your cart before another change.');
      }
      rows.forEach((row) => {
        const line = expectedCart.items.find((item) => item.key === row.dataset.lineKey);
        const roster = row.querySelector('[data-bundle-roster]');
        if (line.has_components !== true && roster) throw new Error('The ordinary pattern display is stale. Reload your cart.');
        if (line.properties?._aov_offerType === 'bundle-builder' && roster?.dataset.componentsPriced === 'true') {
          validateNativeBundleRoster(line, bundleSelectionIds(line.properties._aov_bundleConfiguration),
            JSON.parse(roster.querySelector('[data-cart-native-members]')?.textContent || 'null'), expectedCart.currency);
        }
      });
    };
    if (drawer && freshDrawer) validateRows(freshDrawer);
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && (typeof sections['cart-icon-bubble'] !== 'string' || !sections['cart-icon-bubble'].trim())) {
      throw new Error('Cart display incomplete. Reload your cart.');
    }
    const updates = [];
    for (const part of descriptors) {
      const target = document.getElementById(part.id);
      const source = parse(sections[part.section]).querySelector(part.selector);
      if (target && !source) throw new Error('Cart display incomplete. Reload your cart.');
      if (target && part.id === 'main-cart-items') validateRows(source);
      if (target) updates.push({ target: target.querySelector(part.selector) || target, html: source.innerHTML });
    }
    // Validate ALL fragments before changing any row, total or count. Never
    // leave a freshly charged cart behind a partially updated/stale quote.
    if (drawer && freshDrawer) {
      drawer.closest('cart-drawer')?.clearAddedState?.();
      drawer.innerHTML = freshDrawer.innerHTML;
      const owner = drawer.closest('cart-drawer');
      owner?.classList.toggle('is-empty', !!drawer.querySelector('.drawer__inner-empty'));
      drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => owner?.close());
    }
    updates.forEach(({ target, html }) => { target.innerHTML = html; });
    if (bubble && sections['cart-icon-bubble']) bubble.innerHTML = parse(sections['cart-icon-bubble']).body.innerHTML;
    if (pageCart) {
      const empty = !pageCart.querySelector('.cart-item');
      pageCart.classList.toggle('is-empty', empty);
      pageCart.classList.toggle('miniami-parallel-cart-items--populated', !empty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    }
    blockMemberCheckout(memberOperationPending || commercePending > 0);
    initAll();
  };
  // One queue owns the native parent and its changing opaque key. Intents are
  // accepted synchronously; DOM disappearance is not a claim about native money.
  const memberGroups = new Map();
  let memberPendingObserver = null;
  let memberAcknowledgedCart = null;
  const memberHasWork = (group) => !group.done && (group.all || group.ids.some((id) => group.removed.has(id)));
  const paintMemberPending = () => {
    all(document, '[data-member-optimistic]').forEach((node) => {
      delete node.dataset.memberOptimistic; node.hidden = false;
    });
    all(document, '[data-member-pending]').forEach((node) => { delete node.dataset.memberPending; });
    for (const group of new Set(memberGroups.values())) {
      all(document, '.cart-item[data-line-key]').filter((row) => memberGroups.get(row.dataset.lineKey) === group).forEach((row) => {
        row.dataset.memberPending = 'true';
        if (group.all) { row.hidden = true; row.dataset.memberOptimistic = 'true'; }
        else row.querySelectorAll('[data-bundle-selection]').forEach((member) => {
          if (group.removed.has(member.dataset.selectionVariantId)) {
            member.hidden = true; member.dataset.memberOptimistic = 'true';
          }
        });
      });
    }
    all(document, cartRootSelector).forEach((root) => {
      let status = root.querySelector('[data-member-pending-status]');
      if (memberOperationPending && !status) {
        status = document.createElement('p'); status.dataset.memberPendingStatus = '';
        status.className = 'miniami-cart-bundle-roster__warning'; status.setAttribute('role', 'status');
        status.textContent = 'Updating cart… Totals will refresh when confirmed.';
        (root.querySelector('.drawer__inner') || root).append(status);
      } else if (!memberOperationPending) status?.remove();
    });
    if (memberOperationPending) blockMemberCheckout(true);
  };
  const runMemberGroup = async (group) => {
    while (memberHasWork(group)) {
      const before = await readCart();
      let outcome;
      if (group.ordinary) {
        const line = before.items.find((item) => item.key === group.key);
        if (!line || (group.ordinary === true ? line.has_components === true || line.properties?._aov_offerType === 'bundle-builder' : stableCartValue(line) !== stableCartValue(group.ordinary))) throw new Error('The remaining pattern changed. Refresh your cart.');
        outcome = await removeExactCartLine(before, group.key);
      } else {
        const current = resolveBundleContinuation(before, group.key, group.config);
        if (!current || JSON.stringify(current.ids) !== JSON.stringify(group.ids)) throw new Error('This bundle changed. Refresh your cart.');
        const native = await nativeRosterFromSections(current.key);
        // Coalesce clicks that arrived during the reads. A later click during
        // POST is retained and resolved against the ACK's NEW parent key.
        let remaining = current.ids.filter((id) => !group.removed.has(id));
        if (group.all || remaining.length === 0) {
          outcome = await removeBundleContinuation(before, current, group.config);
        } else {
          const line = before.items.find((item) => item.key === current.key);
          validateNativeBundleRoster(line, current.ids, native, before.currency);
          if (remaining.length === 1 && current.ids.length > 2) {
            // Reach the already-qualified two-to-one bridge without ever
            // submitting a one-component discounted parent configuration.
            remaining = [remaining[0], current.ids.find((id) => id !== remaining[0])].sort();
          }
          if (remaining.length === 1) {
            const intent = { key: current.key, ids: current.ids, offerId: group.config.offerId,
              memberId: current.ids.find((id) => id !== remaining[0]) };
            outcome = await updateBundleDissolution(before, intent, group.config, native);
          } else {
            outcome = await updateBundleContinuation(before, current, group.config,
              remaining.map((id) => ({ id: Number(id), price: 0 })));
          }
        }
      }
      group.key = outcome.continuation?.key || outcome.survivorKey || group.key;
      memberGroups.set(group.key, group);
      group.ids = outcome.continuation?.ids || (outcome.survivorKey
        ? [String(outcome.cart.items.find((line) => line.key === outcome.survivorKey).variant_id)] : []);
      group.ordinary = outcome.survivorKey ? outcome.cart.items.find((line) => line.key === outcome.survivorKey) : null;
      if (outcome.removed) group.done = true;
      group.confirmed = group.initialIds.filter((id) => !group.ids.includes(id));
      group.confirmedAll = outcome.removed === true;
      memberAcknowledgedCart = outcome.cart;
    }
  };
  const drainMemberRemovals = async () => {
    let error = '', recoveryRequired = false, displayConfirmed = false;
    try {
      do {
        for (const group of new Set(memberGroups.values())) if (memberHasWork(group)) await runMemberGroup(group);
        await refreshMemberSections(memberAcknowledgedCart);
        // A click arriving while sections load belongs to this same queue.
      } while ([...new Set(memberGroups.values())].some(memberHasWork));
      displayConfirmed = true;
    } catch (failure) {
      recoveryRequired = failure.bundleRecoveryRequired === true;
      error = failure.message || 'Could not confirm this change. Reload your cart.';
      // Undo only unconfirmed optimism. Already ACKed removals stay hidden if
      // stale sections fail validation; never resurrect them as a successful UI.
      for (const group of new Set(memberGroups.values())) {
        group.removed = new Set(group.confirmed || []); group.all = group.confirmedAll === true; group.done = true;
      }
      paintMemberPending();
      try { await refreshMemberSections(); displayConfirmed = true; } catch (refreshError) {
        recoveryRequired = true; error += ' Cart display could not refresh.';
      }
    } finally {
      memberPendingObserver?.disconnect(); memberPendingObserver = null;
      if (displayConfirmed) memberGroups.clear();
      memberOperationPending = false;
      paintMemberPending(); blockMemberCheckout(recoveryRequired || commercePending > 0);
      all(document, cartRootSelector).forEach((root) => {
        const message = document.createElement('p'); message.dataset.memberOperationResult = '';
        message.className = error ? 'miniami-cart-bundle-roster__warning' : 'visually-hidden';
        message.setAttribute('role', error ? 'alert' : 'status');
        message.textContent = error || 'Removed. Your cart has been updated.';
        if (error) {
          const reload = document.createElement('a'); reload.href = route('cart_url', '/cart');
          reload.textContent = 'Reload cart'; message.append(' ', reload);
        }
        (root.querySelector('.drawer__inner') || root).append(message);
        if (root.matches('cart-drawer')) root.syncContents?.();
      });
    }
  };
  const onMemberRemove = (event) => {
    const button = event.target.closest?.('[data-cart-member-remove], [data-cart-bundle-remove], cart-remove-button');
    if (!button) return;
    if (button.matches('cart-remove-button') && !button.closest('cart-items, cart-drawer-items')) return;
    event.preventDefault(); event.stopImmediatePropagation();
    if (memberDisplayUnconfirmed && !memberOperationPending) return;
    if (!memberOperationPending && document.querySelector('cart-items[aria-busy="true"], cart-drawer-items[aria-busy="true"], .cart__items--disabled, [data-miniami-parallel-bundle] [data-busy="true"], [data-miniami-upsell-submit][data-busy="true"]')) return;
    const roster = button.closest('[data-bundle-roster]');
    const key = button.closest('.cart-item[data-line-key]')?.dataset.lineKey;
    if (!key) return;
    const ordinary = button.matches('cart-remove-button');
    if (!roster && !ordinary) return;
    let group = memberGroups.get(key);
    if (!group) {
      if (ordinary) {
        group = {key, ordinary:true, ids:[], initialIds:[], removed:new Set(), all:true, done:false};
        memberGroups.set(key, group);
      } else {
      const config = parseAovConfig(roster.querySelector('[data-cart-member-config]')?.textContent);
      if (!config) return;
      const ids = [...roster.querySelectorAll('[data-bundle-selection]')].map((row) => row.dataset.selectionVariantId).sort();
      group = { key, config, ids, initialIds: [...ids], removed: new Set(), all: false, done: false };
      memberGroups.set(key, group);
      }
    } else if (ordinary) return;
    if (!ordinary && (group.done || group.all)) return;
    if (button.hasAttribute('data-cart-bundle-remove')) group.all = true;
    else if (!ordinary) {
      const id = button.dataset.cartMemberRemove;
      if (group.removed.has(id) || !group.ids.includes(id)) return;
      group.removed.add(id);
    }
    const starting = !memberOperationPending;
    memberOperationPending = true;
    all(document, '[data-member-operation-result]').forEach((node) => node.remove());
    paintMemberPending();
    // Move focus off a disappearing row without blocking a rapid next removal.
    const root = button.closest(cartRootSelector);
    const focus = [...(root?.querySelectorAll('[data-cart-member-remove], [data-cart-bundle-remove], .drawer__close') || [])]
      .find((node) => !node.closest('[hidden]') && !node.disabled);
    focus?.focus();
    if (starting) {
      memberAcknowledgedCart = null;
      document.dispatchEvent(new CustomEvent('miniami:cart-mutation-start'));
      if (typeof MutationObserver === 'function') {
        memberPendingObserver = new MutationObserver(() => paintMemberPending());
        memberPendingObserver.observe(document.body, { childList: true, subtree: true });
      }
      void drainMemberRemovals();
    }
  };
  // Capture serialization includes ordinary quantity/remove/upsell/builder actions
  // while the owned transaction is pending, without disabling unrelated rows.
  const guardMemberOperation = (event) => {
    const removal = event.type === 'click' && event.target.closest?.('[data-cart-member-remove], [data-cart-bundle-remove], cart-remove-button');
    if (removal && memberOperationPending) return; // enqueue, never drop rapid removals
    const mutation = event.type !== 'click' || event.target.closest?.('cart-remove-button, quantity-input, [data-cart-member-remove], [data-cart-bundle-remove], [data-miniami-upsell-submit], [data-bundle-add], [data-bundle-pick], [data-bundle-remove], button[name="checkout"], .cart__dynamic-checkout-buttons');
    const ordinaryPending = !!document.querySelector('cart-items[aria-busy="true"], cart-drawer-items[aria-busy="true"], [data-cart-sync-unconfirmed]');
    if ((memberOperationPending || memberDisplayUnconfirmed || ordinaryPending || commercePending) && mutation && event.target.closest?.(`${memberSurfaceSelector}, [data-miniami-parallel-bundle], [data-builder-nav-dock]`)) {
      event.preventDefault(); event.stopImmediatePropagation();
    }
  };
  document.addEventListener('click', guardMemberOperation, true);
  document.addEventListener('submit', guardMemberOperation, true);
  document.addEventListener('change', guardMemberOperation, true);
  document.addEventListener('click', onMemberRemove, true);

  const firstFocusable = (root) => root?.querySelector(
    'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  );

  // Per-item bundle price for one selected row (comment 12: Julia's design shows
  // each pattern's regular price next to its bundle price so the discount is
  // visible per item). Mirrors planAovBundle: only variants the active native
  // offer really discounts (eligible AND a reached tier) get a bundle price;
  // everything else keeps its plain individual price. `percent` is 0 whenever the
  // offer/config is not proven, so no figure is invented — the same fail-closed
  // gate the summary totals already use.
  function renderSelectedPrice(root, price, item, percent) {
    const originalCents = Number(item.price) || 0;
    const pct = Number(percent) > 0 ? Number(percent) : 0;
    const bundleCents = pct > 0 ? Math.round(originalCents * (100 - pct) / 100) : originalCents;
    price.replaceChildren();
    if (bundleCents >= originalCents) {
      price.textContent = formatMoney(originalCents, root.dataset.currency);
      price.dataset.bundleLineDiscount = '0';
      return;
    }
    // <s> for the superseded regular price, matching snippets/price.liquid's
    // struck compare-at pair (visually-hidden labels for assistive tech).
    const wasLabel = document.createElement('span');
    wasLabel.className = 'visually-hidden visually-hidden--inline';
    wasLabel.textContent = textFor(root, 'regularPrice');
    const was = document.createElement('s');
    was.className = 'miniami-parallel-bundle__selected-price-was';
    was.textContent = formatMoney(originalCents, root.dataset.currency);
    const nowLabel = document.createElement('span');
    nowLabel.className = 'visually-hidden visually-hidden--inline';
    nowLabel.textContent = textFor(root, 'bundlePrice');
    const now = document.createElement('span');
    now.className = 'miniami-parallel-bundle__selected-price-now';
    now.textContent = formatMoney(bundleCents, root.dataset.currency);
    price.append(wasLabel, was, nowLabel, now);
    price.dataset.bundleLineDiscount = String(pct);
  }

  function makeSelectedLine(root, item, key, percent = 0) {
    const line = document.createElement('li');
    line.className = 'miniami-parallel-bundle__selected-line';
    if (item.imageUrl) {
      const media = document.createElement('span');
      media.className = 'miniami-parallel-bundle__selected-media';
      const image = document.createElement('img');
      image.src = item.imageUrl;
      image.alt = '';
      image.width = 44;
      image.height = 44;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.addEventListener('error', () => media.remove(), { once: true });
      media.appendChild(image);
      line.appendChild(media);
    }
    const copy = document.createElement('span');
    copy.className = 'miniami-parallel-bundle__selected-copy';
    const title = document.createElement('strong');
    title.textContent = window.MiniamiTitleBase(item.title);
    const price = document.createElement('small');
    price.className = 'miniami-parallel-bundle__selected-price';
    renderSelectedPrice(root, price, item, percent);
    copy.append(title, price);
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'miniami-parallel-bundle__remove';
    remove.dataset.bundleRemove = key;
    remove.innerHTML = '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6L6 18"></path></svg>';
    remove.setAttribute('aria-label', `${textFor(root, 'remove')} ${window.MiniamiTitleBase(item.title)}`);
    line.append(copy, remove);
    return line;
  }

  function initBundle(root) {
    if (bundleControllers.has(root)) return bundleControllers.get(root);

    let dock = null;
    const ownedAll = (selector) => [...all(root, selector), ...(dock && !root.contains(dock) ? all(dock, selector) : [])];
    const cards = ownedAll('[data-bundle-card]');
    const aovConfig = parseAovConfig(root.querySelector('[data-bundle-aov-config]')?.textContent);
    const tiers = aovConfig?.tiers || parseTiers(root);
    const selected = new Map();
    const selectedLines = root.querySelector('[data-bundle-selected-lines]');
    const summaries = ownedAll('[data-bundle-summary]');
    const summaryPanel = root.querySelector('.miniami-parallel-bundle__summary');
    const summaryHeading = root.querySelector('[data-bundle-summary-heading]');
    const mobileSummary = root.querySelector('[data-bundle-mobile-summary]');
    const mobileTotal = root.querySelector('[data-bundle-mobile-total]');
    const mobileSubtotal = mobileTotal?.querySelector('[data-bundle-mobile-subtotal]');
    const mobilePrice = mobileTotal?.querySelector('[data-bundle-mobile-price]');
    const addButtons = ownedAll('[data-bundle-add]');
    const status = root.querySelector('[data-bundle-selection-status]');
    const offerNote = root.querySelector('[data-bundle-offer-note]');
    const discountLabel = root.querySelector('[data-bundle-discount-label]');
    const originalDiscountLabel = discountLabel?.textContent || 'Bundle discount';
    root.dataset.aovReady = String(!!aovConfig);
    const subtotal = root.querySelector('[data-bundle-subtotal]');
    const discount = root.querySelector('[data-bundle-discount]');
    const total = root.querySelector('[data-bundle-total]');
    const dialog = root.querySelector('[data-bundle-addon-dialog]');
    const originalLabels = new Map(addButtons.map((button) => [button, button.textContent.trim()]));
    const setAddButtonLabel = (button, label) => {
      const labelElement = button.querySelector('[data-bundle-add-label]');
      if (labelElement) {
        labelElement.textContent = label;
        return;
      }
      button.textContent = label;
    };
    let pendingAddon = null;
    let lastFocused = null;
    let submitting = false;
    let restoring = false;
    let disconnected = false;
    let continuation = null;
    let continuationUncertain = false;
    let continuationRead = null;
    let actualDiscountCents = 0;
    let actualSubtotalCents = null;
    let actualTotalCents = null;
    const summaryAnimations = new Set();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const stopSummaryMotion = () => {
      summaryAnimations.forEach((animation) => animation.cancel());
      summaryAnimations.clear();
    };
    const animateSummary = (element, frames) => {
      if (!element?.animate || reducedMotion.matches || document.hidden
        || navigator.connection?.saveData || document.documentElement.dataset.miniamiSaveData === 'true') return;
      const animation = element.animate(frames, { duration: 300, easing: 'ease' });
      summaryAnimations.add(animation);
      animation.finished.catch(() => undefined).finally(() => summaryAnimations.delete(animation));
      return animation;
    };
    reducedMotion.addEventListener('change', stopSummaryMotion);

    const cardFor = (variantId, productId) => cards.find((card) => (
      (variantId && card.dataset.variantId === String(variantId))
      || (productId && card.dataset.productId === String(productId))
    ));

    const itemFromCard = (card) => {
      const id = Number.parseInt(card?.dataset.variantId || '', 10);
      if (!card || !Number.isFinite(id) || card.dataset.available === 'false') return null;
      return {
        id,
        title: card.dataset.productTitle || '',
        price: Number.parseInt(card.dataset.priceCents || '0', 10) || 0,
        productUrl: card.dataset.productUrl || '',
        imageUrl: card.dataset.productImage || '',
        properties: { bundle: root.dataset.bundleProperty || 'pick-and-mix' },
      };
    };

    const itemFromParent = (card) => {
      const id = Number.parseInt(card?.dataset.addonParentVariantId || '', 10);
      if (!card || !Number.isFinite(id)) return null;
      return {
        id,
        title: card.dataset.addonParentTitle || textFor(root, 'requiredParent'),
        price: Number.parseInt(card.dataset.addonParentPriceCents || '0', 10) || 0,
        productUrl: card.dataset.addonParentUrl || '',
        imageUrl: card.dataset.addonParentImage || '',
        properties: { bundle: root.dataset.bundleProperty || 'pick-and-mix' },
      };
    };

    const selectedSubtotal = () => [...selected.values()].reduce((sum, item) => sum + item.price, 0);

    const invalidatePricing = () => {
      actualDiscountCents = 0;
      actualSubtotalCents = null;
      actualTotalCents = null;
      root.dataset.discountProven = 'false';
    };

    const setStatus = (message, role = 'status') => {
      if (!status) return;
      status.textContent = message;
      status.classList.toggle('visually-hidden', role !== 'alert');
      status.setAttribute('role', role);
      status.setAttribute('aria-live', role === 'alert' ? 'assertive' : 'polite');
    };

    const showError = (message) => {
      ownedAll('[data-bundle-error]').forEach((element) => {
        element.textContent = message;
        element.hidden = false;
        element.setAttribute('role', 'alert');
        element.setAttribute('aria-live', 'assertive');
      });
      setStatus(message, 'alert');
    };

    const closeDialog = () => {
      if (!dialog) return;
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
      lastFocused?.focus?.();
      lastFocused = null;
      pendingAddon = null;
    };

    const dismissDialog = () => {
      if (!pendingAddon) {
        closeDialog();
        return;
      }
      resolveAddon(false);
    };

    const openDialog = (card) => {
      if (!dialog) return false;
      pendingAddon = card;
      lastFocused = document.activeElement;
      const title = dialog.querySelector('[data-addon-parent-title]');
      const heading = dialog.querySelector('[data-addon-warning-heading]');
      const ownButton = dialog.querySelector('[data-addon-own]');
      const price = dialog.querySelector('[data-addon-parent-price]');
      const message = dialog.querySelector('[data-addon-message]');
      const media = dialog.querySelector('[data-addon-parent-media]');
      const image = dialog.querySelector('[data-addon-parent-image]');
      if (title) title.textContent = card.dataset.addonParentTitle || textFor(root, 'requiredParent');
      if (heading) heading.textContent = card.dataset.addonWarningHeading || heading.dataset.addonDefaultHeading || '';
      if (ownButton) ownButton.textContent = card.dataset.addonOwnLabel || ownButton.dataset.addonDefaultLabel || '';
      if (price) price.textContent = card.dataset.addonParentPrice || '';
      if (message) {
        const messageText = card.dataset.addonMessage || message.dataset.addonDefaultMessage || '';
        message.textContent = messageText;
        message.hidden = !messageText;
      }
      if (media && image) {
        const imageUrl = card.dataset.addonParentImage || '';
        if (imageUrl) {
          image.src = imageUrl;
          image.alt = card.dataset.addonParentImageAlt || '';
          media.hidden = false;
        } else {
          image.removeAttribute('src');
          image.alt = '';
          media.hidden = true;
        }
      }
      if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
      else dialog.setAttribute('open', '');
      firstFocusable(dialog)?.focus();
      return true;
    };

    const onDialogKeydown = (event) => {
      if (!dialog || event.key !== 'Tab') return;
      const focusables = all(dialog, 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const choose = (item, announce = false) => {
      if (!item || !Number.isFinite(item.id)) return;
      selected.set(String(item.id), item);
      if (announce) setStatus(`${selected.size} ${textFor(root, 'selected')}`);
    };

    const setPickLabel = (button) => {
      const labels = {
        add: button.querySelector('[data-bundle-label-add]'),
        added: button.querySelector('[data-bundle-label-added]'),
        remove: button.querySelector('[data-bundle-label-remove]'),
      };
      if (!labels.add || !labels.added || !labels.remove) {
        return false;
      }
      labels.add.textContent = textFor(root, 'add');
      labels.added.textContent = textFor(root, 'added');
      labels.remove.textContent = textFor(root, 'remove');
      return true;
    };

    const render = () => {
      const previousHeight = summaryPanel?.getBoundingClientRect().height || 0;
      const previousCount = root.dataset.selectionCount;
      stopSummaryMotion();
      const count = selected.size;
      const plan = planAovBundle(aovConfig, [...selected.values()]);
      const tier = aovConfig ? plan.tier : tierFor(tiers, count);
      const subtotalCents = actualSubtotalCents ?? selectedSubtotal();
      const backendProven = root.dataset.discountProven === 'true';
      const discountCents = backendProven ? actualDiscountCents : plan.discount;
      const totalCents = actualTotalCents ?? Math.max(0, subtotalCents - discountCents);
      root.dataset.eligibleCount = String(plan.eligibleCount);
      root.dataset.selectionCount = String(count);
      root.dataset.selectionTier = String(tier.min);
      root.style.setProperty('--bundle-progress', `${(tiers.findIndex((stop) => stop.min === tier.min) + 1) / tiers.length * 100}%`);
      // This original ladder moves outside root into the header. Keep its
      // current presentation bound on every render, not only when docking.
      ladder?.style.setProperty('--bundle-progress', root.style.getPropertyValue('--bundle-progress'));
      if (ladder) ladder.dataset.progressActive = String(backendProven || (root.dataset.aovReady === 'true' && count > 0));
      summaryPanel?.classList.toggle('is-active', count > 0);
      if (summaryHeading) summaryHeading.hidden = false;

      cards.forEach((card) => {
        const picked = selected.has(String(card.dataset.variantId));
        card.classList.toggle('is-picked', picked);
        const button = card.querySelector('[data-bundle-pick]');
        const addonBadge = card.querySelector('[data-bundle-addon-state]');
        if (addonBadge) {
          addonBadge.textContent = picked ? addonBadge.dataset.selectedLabel : addonBadge.dataset.unselectedLabel;
          addonBadge.classList.toggle('is-selected', picked);
        }
        const cardPrice = card.querySelector('[data-bundle-card-price]');
        if (cardPrice) {
          const item = itemFromCard(card);
          const eligible = !!item && !!aovConfig && picked && plan.tier.pct > 0
            && Object.prototype.hasOwnProperty.call(aovConfig.eligible, String(item.id));
          const pct = eligible ? plan.tier.pct : 0;
          if (cardPrice.dataset.bundleLineDiscount !== String(pct)
            || cardPrice.dataset.bundleVariantId !== String(item?.id)) {
            if (item) renderSelectedPrice(root, cardPrice, item, pct);
            cardPrice.dataset.bundleVariantId = String(item?.id || '');
          }
          cardPrice.classList.toggle('is-estimated-bundle-price', pct > 0);
        }
        const eligibility = card.querySelector('[data-bundle-eligibility]');
        if (eligibility) {
          const excluded = !!aovConfig && !Object.prototype.hasOwnProperty.call(aovConfig.eligible, card.dataset.variantId);
          eligibility.hidden = !excluded;
          eligibility.textContent = excluded ? textFor(root, 'excludedNote') : '';
        }
        if (!button) return;
        button.setAttribute('aria-pressed', picked ? 'true' : 'false');
        button.setAttribute('aria-label', `${textFor(root, picked ? 'remove' : 'add')} ${card.dataset.productTitle || ''}`.trim());
        if (!setPickLabel(button)) {
          button.textContent = picked ? textFor(root, 'remove') : textFor(root, 'add');
        }
      });

      if (selectedLines) {
        // Per-item bundle price (comment 12): the proven tier percentage, applied only to
        // variants the native offer actually discounts (planAovBundle's `bundled` set).
        // Ineligible/excluded patterns keep their plain individual price.
        const linePercent = (item) => (aovConfig && plan.tier.pct > 0
          && Object.prototype.hasOwnProperty.call(aovConfig.eligible, String(item.id)) ? plan.tier.pct : 0);
        // Preserve existing rows (and keyboard focus) instead of replaying every row.
        const rows = new Map([...selectedLines.children].map((line) => [line.dataset.bundleLine, line]));
        rows.forEach((line, key) => { if (!selected.has(key)) line.remove(); });
        selected.forEach((item, key) => {
          if (rows.has(key)) return;
          const line = makeSelectedLine(root, item, key, linePercent(item));
          line.dataset.bundleLine = key;
          selectedLines.appendChild(line);
          animateSummary(line, [{ opacity: 0, transform: 'translateY(8px)' }, { opacity: 1, transform: 'translateY(0)' }]);
        });
        // The reached tier moves as the bundle grows (4 -> 5 picks changes 20% -> 25%), so
        // refresh retained rows in place: only the price element's children change, never
        // the row, so keyboard focus inside a row survives.
        [...selectedLines.children].forEach((line) => {
          const item = selected.get(line.dataset.bundleLine);
          const price = line.querySelector('.miniami-parallel-bundle__selected-price');
          if (!item || !price) return;
          const percent = linePercent(item);
          if (price.dataset.bundleLineDiscount !== String(percent)) renderSelectedPrice(root, price, item, percent);
        });
      }
      ownedAll('[data-bundle-count]').forEach((element) => {
        const tierCopy = element.closest('[data-bundle-mobile-summary]') && plan.tier.pct > 0
          ? ` · ${plan.tier.pct}% off` : '';
        element.textContent = `${count} ${textFor(root, 'picked')}${tierCopy}`;
      });
      if (subtotal) subtotal.textContent = formatMoney(subtotalCents, root.dataset.currency);
      if (discount) {
        discount.textContent = discountCents > 0
          ? `-${formatMoney(discountCents, root.dataset.currency)}`
          : aovConfig ? '—' : textFor(root, 'offerUnavailable');
      }
      if (discountLabel) discountLabel.textContent = originalDiscountLabel + (plan.tier.pct ? ` · ${plan.tier.pct}%` : '');
      if (offerNote) offerNote.textContent = textFor(root, aovConfig ? 'offerNoteEstimated' : 'offerNoteFallback');
      if (total) total.textContent = formatMoney(totalCents, root.dataset.currency);
      if (mobileSubtotal && mobilePrice) {
        mobileSubtotal.hidden = discountCents <= 0;
        mobileSubtotal.textContent = formatMoney(subtotalCents, root.dataset.currency);
        mobilePrice.textContent = formatMoney(totalCents, root.dataset.currency);
      } else if (mobileTotal) mobileTotal.textContent = formatMoney(totalCents, root.dataset.currency);
      summaries.forEach((summaryRoot) => { summaryRoot.hidden = false; });
      if (mobileSummary) mobileSummary.hidden = false;
      addButtons.forEach((button) => {
        if (button.dataset.busy !== 'true') {
          setAddButtonLabel(button, continuation ? 'Update bundle' : originalLabels.get(button) || textFor(root, 'add'));
        }
        button.disabled = (count === 0 && !continuation) || submitting || restoring || continuationUncertain || root.dataset.bundleContinuation === 'unavailable';
        button.setAttribute('aria-disabled', button.disabled ? 'true' : 'false');
      });
      ownedAll('[data-bundle-tier]').forEach((element) => {
        element.classList.toggle('is-current', count > 0 && Number(element.dataset.bundleTier) === tier.min);
        const nativeTier = aovConfig?.tiers.find((entry) => entry.min === Number(element.dataset.bundleTier));
        const label = element.querySelector('strong');
        if (label) { label.textContent = nativeTier ? `${nativeTier.pct}%` : '—'; label.removeAttribute('aria-hidden'); }
      });
      if (previousCount !== undefined && previousCount !== String(count)) {
        const nextHeight = summaryPanel?.getBoundingClientRect().height || 0;
        if (previousHeight > 0 && nextHeight > 0 && Math.abs(previousHeight - nextHeight) > 1) {
          animateSummary(summaryPanel, [{ height: `${previousHeight}px` }, { height: `${nextHeight}px` }]);
        }
        // Mobile retains the authored compact bar: only its changing figures move.
        if (mobileSummary && window.matchMedia('(max-width: 749px)').matches) {
          animateSummary(mobileSummary.firstElementChild, [{ transform: 'translateY(3px)', opacity: .6 }, { transform: 'translateY(0)', opacity: 1 }]);
        }
      }
    };

    const toggleCard = (card) => {
      const variantId = card?.dataset.variantId;
      if (!variantId || card.dataset.available === 'false') return;
      invalidatePricing();
      if (selected.has(String(variantId))) {
        selected.delete(String(variantId));
        setStatus(`${selected.size} ${textFor(root, 'selected')}`);
      } else choose(itemFromCard(card), true);
      render();
    };

    const resolveAddon = (includeParent) => {
      if (!pendingAddon) return;
      invalidatePricing();
      const parentAvailable = pendingAddon.dataset.addonParentAvailable === 'true';
      const parentCard = cardFor(pendingAddon.dataset.addonParentVariantId, pendingAddon.dataset.addonParentProductId);
      const parentItem = parentCard ? itemFromCard(parentCard) : itemFromParent(pendingAddon);
      if (includeParent && (!parentAvailable || !parentItem)) {
        showError(textFor(root, 'unavailableParent'));
        closeDialog();
        return;
      }
      if (includeParent) choose(parentItem);
      choose(itemFromCard(pendingAddon));
      setStatus(`${selected.size} ${textFor(root, 'selected')}`);
      closeDialog();
      render();
    };

    const selectionFingerprint = () => [...selected.values()]
      .sort((left, right) => left.id - right.id)
      .map((item) => `${item.id}:${item.properties.bundle || ''}`)
      .join('|');

    const configurationKey = (value) => {
      try {
        return JSON.parse(value).flatMap((entry) => Array(Number(entry.q) || 1).fill(String(entry.i))).sort().join('|');
      } catch (error) { return null; }
    };
    const existingLine = (line, item) => {
      if (String(line.variant_id) !== String(item.id)) return false;
      if (item.properties?._aov_bundleConfiguration) {
        return line.properties?._aov_offerId === item.properties._aov_offerId
          && line.properties?._aov_offerType === 'bundle-builder'
          && configurationKey(line.properties?._aov_bundleConfiguration) === configurationKey(item.properties._aov_bundleConfiguration);
      }
      return String(line.properties?.bundle || '') === String(item.properties?.bundle || '');
    };

    const readbackDiscount = (cart, selectedItems) => {
      const plan = planAovBundle(aovConfig, selectedItems);
      const lines = plan.lines.map((item) => (cart.items || []).find((line) => existingLine(line, item)));
      if (lines.some((line) => !line)) throw new Error('missing selected cart line');
      const wrapper = lines.find((line) => line.properties?._aov_offerType === 'bundle-builder');
      if (wrapper && wrapper.has_components !== true) throw new Error('unexpanded bundle');
      actualSubtotalCents = selectedSubtotal();
      // AOV embeds its reduction in original_price. Do not mislabel additional
      // line_level_discount_allocations or unrelated cart discounts as AOV savings.
      actualDiscountCents = wrapper ? Math.max(0, plan.subtotal - Number(wrapper.original_price)) : 0;
      actualTotalCents = lines.reduce((sum, line) => sum + Number(line.final_price), 0);
      root.dataset.discountProven = wrapper ? 'true' : 'false';
    };

    const bindContinuation = (value) => {
      continuation = value;
      const url = new URL(window.location.href);
      url.searchParams.delete('miniami_pick');
      if (value) {
        root.dataset.bundleContinuation = 'restored';
        url.searchParams.set('miniami_edit', value.key);
      } else {
        delete root.dataset.bundleContinuation;
        url.searchParams.delete('miniami_edit');
      }
      window.history.replaceState(window.history.state, '', url.href);
    };
    const submit = async (button) => {
      if (!button || submitting || continuationUncertain || (selected.size === 0 && !continuation) || root.dataset.bundleContinuation === 'unavailable') return;
      const fingerprint = selectionFingerprint();
      if (!fingerprint && !continuation) return;
      submitting = true;
      const finishPendingDrawer = pendingDrawer(button);
      let releaseTransaction;
      const transactionReady = new Promise(resolve => { releaseTransaction = resolve; });
      let beginTransaction;
      const transactionStarted = new Promise(resolve => { beginTransaction = resolve; });
      const transaction = enqueueCommerce(async () => { beginTransaction(); await transactionReady; });
      ownedAll('[data-bundle-error]').forEach((element) => { element.hidden = true; element.replaceChildren(); });
      addButtons.forEach((addButton) => {
        addButton.dataset.busy = 'true';
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');
        addButton.setAttribute('aria-busy', 'true');
        setAddButtonLabel(addButton, textFor(root, 'adding'));
      });
      setStatus(textFor(root, 'adding'));
      let failed = false;
      let added = false;
      let removed = false;
      let drawerFailed = false;
      try {
        await transactionStarted;
        const selectedItems = [...selected.values()];
        const before = await readCart();
        if (continuation) {
          const current = resolveBundleContinuation(before, continuation.key, aovConfig);
          if (!current || current.signature !== continuation.signature) {
            throw new Error('This bundle has changed or was removed. Return to your cart and choose Add more again. Your cart is unchanged.');
          }
          if (selectedItems.some((item) => !cards.some((card) => card.dataset.variantId === String(item.id)
            && card.dataset.available === 'true' && card.dataset.addon === 'false'))) {
            throw new Error('A selected pattern is unavailable. Your original bundle is unchanged.');
          }
          if (selectedItems.length === 0) document.dispatchEvent(new CustomEvent('miniami:cart-mutation-start'));
          const outcome = await updateBundleContinuation(before, current, aovConfig, selectedItems);
          bindContinuation(outcome.continuation);
          readbackDiscount(outcome.cart, selectedItems);
          if (outcome.removed) {
            added = true;
            removed = true;
            await refreshMemberSections(outcome.cart);
            document.querySelector('cart-drawer')?.open?.(button);
            setStatus('Bundle removed. Your cart has been updated.');
          } else if (outcome.already) {
            setStatus(textFor(root, 'alreadyInCart'));
          } else {
            added = true;
            await notifyCartUpdate(outcome.cart, {}, 'miniami-parallel-bundle');
            await openBundleCartDrawer(button);
            setStatus(textFor(root, 'added'));
          }
          return;
        }
        const plan = planAovBundle(aovConfig, selectedItems);
        const missingItems = plan.lines.filter((item) => !(before.items || []).some((line) => existingLine(line, item)));
        if (missingItems.length > 0) {
          let payload = {}, rejected = false;
          try {
            const response = await fetch(`${route('cart_url', '/cart')}/add.js?app=aov-ai-bundle`, {
              method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},
              body:JSON.stringify({items:missingItems.map(item=>({id:item.id,quantity:1,properties:item.properties})),sections:['cart-drawer','cart-icon-bubble'],sections_url:window.location.pathname})
            });
            payload=await response.json().catch(()=>({}));rejected=!response.ok || payload.errors || payload.status>=400;
          } catch (_) { /* Unknown POST ACK: reconcile reads, never repeat the write. */ }
          let after;
          try {
            after=await readCartUntil(cart=>{
              const expected=plan.lines.map(item=>(cart.items||[]).find(line=>existingLine(line,item)));
              if(expected.some(line=>!line))return false;
              const wrapper=expected.find(line=>line.properties?._aov_offerType==='bundle-builder');
              return !wrapper || wrapper.has_components===true;
            });
          } catch (_) { throw Object.assign(new Error('Cart update could not be confirmed. Reload cart before trying again.'),{bundleRecoveryRequired:true}); }
          const allExpected=plan.lines.every(item=>(after.items||[]).some(line=>existingLine(line,item)));
          if(!allExpected){
            if(rejected && stableCartValue(after)===stableCartValue(before))throw new Error('Your cart is unchanged. Please try again.');
            throw Object.assign(new Error('Cart update could not be confirmed. Reload cart before trying again.'),{bundleRecoveryRequired:true});
          }
          const unexpanded = (after.items || []).find((line) => line.properties?._aov_offerType === 'bundle-builder'
            && line.has_components !== true && missingItems.some((item) => existingLine(line, item)));
          if (unexpanded) {
            // Roll back only the new invalid wrapper, never an existing/personal cart.
            const rollback = await fetch(`${route('cart_url', '/cart')}/change.js`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: unexpanded.key, quantity: 0 }),
            });
            if (!rollback.ok) throw new Error('invalid bundle rollback failed');
            const restored = await readCart();
            if ((restored.items || []).some((line) => line.key === unexpanded.key)) throw new Error('invalid bundle remains');
            throw new Error('provider did not expand selected components');
          }
          readbackDiscount(after, selectedItems);
          const addedGroups = after.items.filter((line) => plan.lines.some((item) =>
            item.properties?._aov_offerType === 'bundle-builder' && existingLine(line, item)));
          if (plan.lines.length === 1 && addedGroups.length === 1) {
            bindContinuation(resolveBundleContinuation(after, addedGroups[0].key, aovConfig));
          }
          added = true;
          await notifyCartUpdate(after, payload, 'miniami-parallel-bundle');
          await openBundleCartDrawer(button, payload);
          setStatus(textFor(root, 'added'));
        } else {
          readbackDiscount(before, selectedItems);
          setStatus(textFor(root, 'alreadyInCart'));
        }
      } catch (error) {
        if (error.restoredBundle?.continuation) bindContinuation(error.restoredBundle.continuation);
        if (error.bundleRecoveryRequired) {
          continuationUncertain = true;
          root.dataset.bundleContinuation = 'unconfirmed';
          for(const surface of all(document,cartRootSelector))surface.dataset.cartSyncUnconfirmed='true';
        }
        failed = !added;
        const pendingSurface = document.querySelector('cart-drawer .drawer__inner');
        if (pendingSurface) {
          const message = document.createElement('p'); message.setAttribute('role','alert');
          message.dataset.commerceError = ''; message.textContent = added ? 'Cart saved, but its display could not refresh. Reload cart.' : 'Could not confirm the cart update. Check your cart before trying again.';
          const link = document.createElement('a'); link.href=route('cart_url','/cart'); link.textContent='Reload cart';message.append(' ',link);pendingSurface.prepend(message);
        }
        if (added) {
          drawerFailed = true;
          showError(removed ? 'Bundle removed, but its display could not refresh. Reload your cart.' : textFor(root, 'drawerError'));
          // The desktop summary status and the mobile fixed summary are
          // separate surfaces. Keep a useful cart path on both after an add
          // that succeeded but whose drawer refresh failed.
          [status, ...ownedAll('[data-bundle-error]')].filter(Boolean).forEach((element) => {
            const link = document.createElement('a');
            link.href = route('cart_url', '/cart');
            link.textContent = textFor(root, 'viewCart');
            element.append(' ', link);
          });
        } else {
          showError(continuation ? error.message : textFor(root, 'cartError'));
          if (continuationUncertain) ownedAll('[data-bundle-error]').forEach((element) => {
            const link = document.createElement('a'); link.href = route('cart_url', '/cart');
            link.textContent = textFor(root, 'viewCart'); element.append(' ', link);
          });
        }
      } finally {
        finishPendingDrawer();
        releaseTransaction();
        await transaction;
        if (continuationUncertain || drawerFailed) blockMemberCheckout(true);
        submitting = false;
        addButtons.forEach((addButton) => {
          delete addButton.dataset.busy;
          addButton.removeAttribute('aria-busy');
        });
        if (failed) {
          addButtons.forEach((addButton) => {
            addButton.disabled = continuationUncertain;
            addButton.setAttribute('aria-disabled', String(continuationUncertain));
            setAddButtonLabel(addButton, continuationUncertain ? textFor(root, 'viewCart') : textFor(root, 'retry'));
          });
        } else {
          render();
          if (drawerFailed) addButtons.forEach((addButton) => {
            addButton.disabled = true;
            addButton.setAttribute('aria-disabled', 'true');
            setAddButtonLabel(addButton, textFor(root, 'added'));
          });
        }
      }
    };

    const onClick = (event) => {
      const pick = event.target.closest('[data-bundle-pick]');
      if (restoring || submitting) return;
      if (pick && root.contains(pick)) {
        const card = pick.closest('[data-bundle-card]');
        if (card?.dataset.addon === 'true' && !selected.has(String(card.dataset.variantId))
          && !selected.has(String(card.dataset.addonParentVariantId)) && openDialog(card)) return;
        toggleCard(card);
        return;
      }
      const remove = event.target.closest('[data-bundle-remove]');
      if (remove && root.contains(remove)) {
        invalidatePricing();
        selected.delete(remove.dataset.bundleRemove);
        setStatus(`${selected.size} ${textFor(root, 'selected')}`);
        render();
        return;
      }
      const add = event.target.closest('[data-bundle-add]');
      if (add && (root.contains(add) || dock?.contains(add))) {
        void submit(add);
        return;
      }
      if (event.target.closest('[data-addon-add-both]') && root.contains(event.target)) {
        resolveAddon(true);
        return;
      }
      if (event.target.closest('[data-addon-own]') && root.contains(event.target)) {
        resolveAddon(false);
        return;
      }
      if (event.target.closest('[data-addon-dismiss]') && root.contains(event.target)) dismissDialog();
    };

    const onChange = (event) => {
      const select = event.target.closest('[data-bundle-variant]');
      if (restoring || submitting) return;
      if (!select || !root.contains(select)) return;
      const card = select.closest('[data-bundle-card]');
      const option = select.selectedOptions?.[0];
      if (!card || !option || option.disabled) return;
      const wasSelected = selected.has(String(card.dataset.variantId));
      invalidatePricing();
      selected.delete(String(card.dataset.variantId));
      card.dataset.variantId = option.value;
      card.dataset.priceCents = option.dataset.priceCents || card.dataset.priceCents;
      card.dataset.available = option.dataset.available || 'false';
      const price = card.querySelector('[data-bundle-card-price]');
      if (price && option.dataset.priceDisplay) price.textContent = option.dataset.priceDisplay;
      if (wasSelected) choose(itemFromCard(card));
      render();
    };

    const onCancel = (event) => {
      if (event.target === dialog) {
        event.preventDefault();
        dismissDialog();
      }
    };
    const onDialogClick = (event) => { if (event.target === dialog) dismissDialog(); };

    const ladder = root.querySelector('[data-bundle-ladder]');
    const nav = document.querySelector('[data-miniami-header]');
    const ladderHome = ladder?.parentElement;
    const mobileHome = mobileSummary?.parentElement;
    // A real flow host, outside the sidebar's display:contents/hidden rules.
    // It owns the original controls in stacked layout and reserves their exact
    // flow height while the header owns them. Never measure an empty hidden DIV.
    const marker = document.createElement('div');
    marker.dataset.bundleDockMarker = '';
    const builder = root.querySelector('.miniami-parallel-bundle__builder');
    if (builder && ladderHome) builder.insertBefore(marker, ladderHome);
    let dockFrame = 0;
    if (nav && ladder && mobileSummary) {
      dock = document.createElement('div'); dock.className = 'miniami-builder-nav-dock';
      dock.dataset.builderNavDock = ''; dock.hidden = true;
      nav.append(dock); dock.addEventListener('click', onClick);
    }
    let dockMode = null;
    let dockWidth = 0;
    const dockAnimations = new Set();
    let focusedAction = null;
    const trackDockFocus = (event) => {
      focusedAction = event.target.matches?.('[data-bundle-add]')
        && (root.contains(event.target) || dock?.contains(event.target)) ? event.target : null;
    };
    document.addEventListener('focusin', trackDockFocus);
    const syncDock = () => {
      dockFrame = 0;
      if (!dock || !ladder || !mobileSummary || !builder) return;
      const narrow = getComputedStyle(builder).display !== 'grid';
      const active = document.activeElement;
      const movingFocus = ladder.contains(active) || mobileSummary.contains(active);
      const actionFocus = focusedAction && (active === focusedAction || active === document.body);
      const restoreControlFocus = () => {
        const target = actionFocus ? (narrow ? mobileSummary : ladderHome).querySelector('[data-bundle-add]')
          : movingFocus ? active : null;
        if (target && !target.disabled && document.activeElement !== target) target.focus({preventScroll:true});
      };
      // Re-measure flow at a new width before reserving it, not dock geometry.
      const width = builder.getBoundingClientRect().width;
      if (narrow && (dockMode === null || dockMode === 'sidebar' || width !== dockWidth)) {
        marker.hidden = false;
        marker.style.height = '';
        marker.append(ladder, mobileSummary);
        dock.hidden = true;
        delete nav.dataset.builderDocked;
        dockMode = 'flow';
      }
      dockWidth = width;
      const anchor = marker.getBoundingClientRect();
      const docked = narrow && anchor.width > 0 && anchor.top < nav.getBoundingClientRect().bottom + 8;
      const mode = docked ? 'dock' : narrow ? 'flow' : 'sidebar';
      if (mode === dockMode) {
        restoreControlFocus();
        return;
      }
      dockAnimations.forEach(animation => animation.cancel());
      dockAnimations.clear();
      const before = [ladder, mobileSummary].map(element => element.getBoundingClientRect());
      const previousMode = dockMode;
      dockMode = mode;
      root.dataset.builderDocked = String(docked);
      if (docked) {
        marker.style.height = `${marker.getBoundingClientRect().height}px`;
        dock.append(ladder, mobileSummary);
        dock.hidden = false;
        nav.dataset.builderDocked = 'true';
      } else {
        marker.style.height = '';
        if (narrow) marker.append(ladder, mobileSummary);
        else {
          ladderHome.insertBefore(ladder, ladderHome.querySelector('[data-bundle-summary]'));
          mobileHome.append(mobileSummary);
        }
        marker.hidden = !narrow;
        dock.hidden = true;
        delete nav.dataset.builderDocked;
      }
      // Animate the same original controls from their actual old positions.
      // Layout/reserved space is final immediately; transforms never own state.
      if (previousMode && previousMode !== 'sidebar' && mode !== 'sidebar') {
        [ladder, mobileSummary].forEach((element, index) => {
          const after = element.getBoundingClientRect();
          const animation = animateSummary(element, [
            {transform:`translate(${before[index].left - after.left}px, ${before[index].top - after.top}px)`},
            {transform:'translate(0, 0)'}
          ]);
          if (animation) dockAnimations.add(animation);
        });
      }
      restoreControlFocus();
    };
    const scheduleDock = () => { if (!dockFrame) dockFrame = requestAnimationFrame(syncDock); };
    window.addEventListener('scroll', scheduleDock, {passive:true});
    window.addEventListener('resize', scheduleDock, {passive:true});
    syncDock();

    const syncSummaryHeight = () => {
      if (summaryPanel) root.dataset.summaryTall = String(summaryPanel.scrollHeight + 96 > window.innerHeight);
    };
    const summaryResizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(syncSummaryHeight) : null;
    if (summaryPanel) summaryResizeObserver?.observe(summaryPanel);
    window.addEventListener('resize', syncSummaryHeight);
    root.addEventListener('click', onClick);
    root.addEventListener('change', onChange);
    dialog?.addEventListener('cancel', onCancel);
    dialog?.addEventListener('click', onDialogClick);
    dialog?.addEventListener('keydown', onDialogKeydown);
    const controller = {
      disconnect() {
        disconnected = true;
        window.removeEventListener('scroll', scheduleDock); window.removeEventListener('resize', scheduleDock);
        document.removeEventListener('focusin', trackDockFocus);
        if (dockFrame) cancelAnimationFrame(dockFrame);
        if (ladder && ladderHome) ladderHome.insertBefore(ladder, ladderHome.querySelector('[data-bundle-summary]'));
        if (mobileSummary && mobileHome) mobileHome.append(mobileSummary);
        delete root.dataset.builderDocked;
        dock?.remove(); marker.remove(); if (nav) delete nav.dataset.builderDocked;
        continuationRead?.abort();
        stopSummaryMotion();
        reducedMotion.removeEventListener('change', stopSummaryMotion);
        summaryResizeObserver?.disconnect();
        window.removeEventListener('resize', syncSummaryHeight);
        root.removeEventListener('click', onClick);
        root.removeEventListener('change', onChange);
        dialog?.removeEventListener('cancel', onCancel);
        dialog?.removeEventListener('click', onDialogClick);
        dialog?.removeEventListener('keydown', onDialogKeydown);
        closeDialog();
        bundleControllers.delete(root);
      },
    };
    bundleControllers.set(root, controller);
    const params = new URLSearchParams(window.location.search);
    const editKeys = params.getAll('miniami_edit');
    if (editKeys.length) {
      restoring = true;
      root.dataset.bundleContinuation = 'loading';
      render();
      void (async () => {
        continuationRead = new AbortController();
        const timeout = setTimeout(() => continuationRead.abort(), 8000);
        try {
          if (editKeys.length !== 1 || params.has('miniami_pick')) throw new Error('invalid continuation');
          const restored = resolveBundleContinuation(await readCart({ signal: continuationRead.signal }), editKeys[0], aovConfig);
          if (!restored) throw new Error('stale continuation');
          const staged = restored.ids.map((id) => {
            const matches = cards.flatMap((card) => {
              const options = [...card.querySelectorAll('[data-bundle-variant] option')].filter((entry) => entry.value === id);
              if (options.length) return options.map((option) => ({ card, option }));
              return card.dataset.variantId === id ? [{ card, option: null }] : [];
            });
            if (matches.length !== 1) throw new Error('unresolved member');
            const { card, option } = matches[0];
            const price = option ? option.dataset.priceCents : card.dataset.priceCents;
            if (card.dataset.addon !== 'false' || !/^[1-9][0-9]*$/.test(card.dataset.productId || '')
              || !/^[0-9]+$/.test(price || '') || !Number.isSafeInteger(Number(price))
              || (option ? option.disabled || option.dataset.available !== 'true'
                : card.dataset.available !== 'true')) throw new Error('unavailable member');
            return { card, option, id };
          });
          if (new Set(staged.map(({ card }) => card)).size !== staged.length) throw new Error('ambiguous product variants');
          if (disconnected) return;
          staged.forEach(({ card, option, id }) => {
            if (option) {
              card.querySelector('[data-bundle-variant]').value = id;
              card.dataset.variantId = id;
              card.dataset.priceCents = option.dataset.priceCents;
              card.dataset.available = option.dataset.available;
            }
            choose(itemFromCard(card));
          });
          continuation = restored;
          root.dataset.bundleContinuation = 'restored';
          setStatus(`${selected.size} ${textFor(root, 'selected')}`);
        } catch (error) {
          if (disconnected) return;
          root.dataset.bundleContinuation = 'unavailable';
          showError('This bundle could not be restored. Return to your cart and choose Add more again. Your cart is unchanged.');
        } finally {
          clearTimeout(timeout);
          restoring = false;
          if (!disconnected) render();
        }
      })();
      return controller;
    }
    // Fox12 C2: intake a single exact native eligible variant, selection only.
    // Never clear existing picks, invoke Add, accept add-ons or invent a product.
    const originPicks = new URLSearchParams(window.location.search).getAll('miniami_pick');
    if (originPicks.length === 1 && /^[1-9][0-9]*$/.test(originPicks[0])
      && aovConfig && Object.prototype.hasOwnProperty.call(aovConfig.eligible, originPicks[0])) {
      const originCard = cards.find(card => card.dataset.variantId === originPicks[0]
        && card.dataset.addon === 'false');
      choose(itemFromCard(originCard));
    }
    render();
    return controller;
  }

  const pendingUpsellIDs = new Set();
  function initUpsellForms(scope = document) {
    rootsFor(scope, cartRootSelector).forEach((root) => {
      if (upsellControllers.has(root)) return;
      const submitUpsell = async (form, button) => {
        const status = form.querySelector('[data-upsell-status]');
        if (!button || button.dataset.busy === 'true' || button.dataset.added === 'true') return;
        const intentID = form.dataset.upsellVariantId || button.dataset.upsellVariantId;
        if (pendingUpsellIDs.has(intentID)) return;
        pendingUpsellIDs.add(intentID);
        button.dataset.busy = 'true';
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
        button.textContent = button.dataset.busyLabel || textFor(form, 'adding');
        if (status) {
          status.textContent = 'Adding… Totals will refresh when confirmed.';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
        }
        try {
          await enqueueCommerce(async () => {
          const id = Number.parseInt(
            form.dataset.upsellVariantId || button.dataset.upsellVariantId || '',
            10,
          );
          if (!Number.isFinite(id)) throw new Error('missing upsell variant');
          const before = await readCart();
          const quantity = cart => (cart.items || []).filter(line=>String(line.variant_id)===String(id) && !line.has_components).reduce((n,line)=>n+line.quantity,0);
          let payload={}, rejected=false;
          try {
            const response=await fetch(route('cart_add_url','/cart/add.js'),{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items:[{id,quantity:1}],sections:['cart-drawer','cart-icon-bubble'],sections_url:window.location.pathname})});
            payload=await response.json().catch(()=>({})); rejected=!response.ok || payload.errors || payload.status>=400;
          } catch (_) { /* Read-only reconciliation of a lost ACK, never POST retry. */ }
          let cart;
          try { cart=await readCart(); }
          catch (_) { throw Object.assign(new Error('Add could not be confirmed. Reload cart; no request was repeated.'),{bundleRecoveryRequired:true}); }
          if(quantity(cart)!==quantity(before)+1){
            if(rejected && stableCartValue(cart)===stableCartValue(before))throw Error('Not added. Your cart is unchanged. Please try again.');
            throw Object.assign(new Error('Add could not be confirmed. Reload cart; no request was repeated.'),{bundleRecoveryRequired:true});
          }
          try { await refreshMemberSections(cart); }
          catch (_) { throw Object.assign(new Error('Added, but the cart display could not refresh. Reload cart before checkout.'),{bundleRecoveryRequired:true}); }
          document.querySelector('cart-drawer')?.syncContents?.();
          if (status) {
            status.textContent = textFor(form, 'added');
            status.setAttribute('role', 'status');
          }
          button.textContent = textFor(form, 'added');
          button.dataset.added = 'true';
          });
        } catch (error) {
          if(error.bundleRecoveryRequired){
            for(const surface of all(document,cartRootSelector))surface.dataset.cartSyncUnconfirmed='true';
            blockMemberCheckout(true);
          }
          let feedback=status?.isConnected?status:root.querySelector('[data-upsell-action-error]');
          if(!feedback){feedback=document.createElement('p');feedback.dataset.upsellActionError='';(root.querySelector('.drawer__inner')||root).prepend(feedback);}
          feedback.textContent=error.bundleRecoveryRequired?error.message:textFor(form,'cartError');
          feedback.setAttribute('role','alert');feedback.setAttribute('aria-live','assertive');
          if(error.bundleRecoveryRequired){const link=document.createElement('a');link.href=route('cart_url','/cart');link.textContent='Reload cart';feedback.append(' ',link);button.dataset.added='true';}
          button.textContent=error.bundleRecoveryRequired?'Check cart':textFor(form,'retry');
        } finally {
          pendingUpsellIDs.delete(intentID);
          delete button.dataset.busy;
          button.removeAttribute('aria-busy');
          if (button.dataset.added !== 'true') button.setAttribute('aria-disabled', 'false');
        }
      };
      const onClick = (event) => {
        const button = event.target.closest?.('[data-miniami-upsell-submit]');
        if (!button || !root.contains(button)) return;
        const form = button.closest('[data-miniami-upsell-form]');
        if (!form) return;
        event.preventDefault();
        void submitUpsell(form, button);
      };
      const onSubmit = (event) => {
        const form = event.target.closest?.('[data-miniami-upsell-form]');
        if (!form || !root.contains(form)) return;
        event.preventDefault();
        const button = form.querySelector('[data-miniami-upsell-submit], button[type="submit"]');
        void submitUpsell(form, button);
      };
      root.addEventListener('click', onClick);
      root.addEventListener('submit', onSubmit);
      upsellControllers.set(root, {
        disconnect() {
          root.removeEventListener('click', onClick);
          root.removeEventListener('submit', onSubmit);
          upsellControllers.delete(root);
        },
      });
    });
  }

  function initCartPills(scope = document) {
    rootsFor(scope, '[data-cart-mobile-pill]').forEach((pill) => {
      if (pillControllers.has(pill)) return;
      const container = pill.closest('.miniami-parallel-cart-footer, .miniami-parallel-cart-drawer') || document;
      let checkoutIntersectionObserver;
      let pillIntersectionObserver;
      let mutationObserver;
      let frame = 0;
      let observedCheckout = null;
      let observedPill = null;

      const setPillState = (visible) => {
        pill.hidden = !visible;
        pill.setAttribute('aria-hidden', visible ? 'false' : 'true');
        pill.dataset.cartPillIntersecting = visible ? 'true' : 'false';
        if (container.dataset) container.dataset.cartPillVisible = visible ? 'true' : 'false';
      };

      const isRendered = (element) => {
        if (!element || element.hidden) return false;
        let ancestor = element;
        while (ancestor && ancestor !== document.documentElement) {
          if (ancestor.hidden || ancestor.getAttribute('aria-hidden') === 'true') return false;
          const styles = window.getComputedStyle(ancestor);
          if (styles.display === 'none' || styles.visibility === 'hidden' || styles.visibility === 'collapse' || styles.contentVisibility === 'hidden') return false;
          if (styles.display !== 'contents') {
            const ancestorRect = ancestor.getBoundingClientRect();
            if (ancestorRect.width <= 0 || ancestorRect.height <= 0) return false;
          }
          ancestor = ancestor.parentElement;
        }
        const rect = element.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      };

      const isInViewport = (element) => {
        if (!isRendered(element)) return false;
        const rect = element.getBoundingClientRect();
        return rect.top >= -0.5
          && rect.bottom <= window.innerHeight + 0.5
          && rect.left >= -0.5
          && rect.right <= window.innerWidth + 0.5;
      };

      const findCheckout = () => rootsFor(container, '[data-cart-primary-checkout]')
        .find((candidate) => !candidate.disabled
          && candidate.getAttribute('aria-disabled') !== 'true'
          && isRendered(candidate)) || null;

      const hasPopulatedCart = () => {
        const cartRoots = rootsFor(document, cartRootSelector);
        if (!cartRoots.length || cartRoots.some((root) => root.classList.contains('is-empty'))) return false;
        return cartRoots.some((root) => root.querySelector('.cart-item, [data-cart-line], [data-cart-item]'));
      };

      const bindCheckoutObserver = () => {
        const checkout = findCheckout();
        if (checkout === observedCheckout) return;
        checkoutIntersectionObserver?.disconnect();
        checkoutIntersectionObserver = undefined;
        observedCheckout = checkout || null;
        if ('IntersectionObserver' in window && checkout) {
          checkoutIntersectionObserver = new IntersectionObserver(schedule, { threshold: [0, 0.01] });
          checkoutIntersectionObserver.observe(checkout);
        }
      };

      const bindPillObserver = () => {
        const nextPill = pill.hidden ? null : pill;
        if (nextPill === observedPill) return;
        pillIntersectionObserver?.disconnect();
        pillIntersectionObserver = undefined;
        observedPill = nextPill;
        if ('IntersectionObserver' in window && nextPill) {
          pillIntersectionObserver = new IntersectionObserver(schedule, { threshold: [0, 0.01, 1] });
          pillIntersectionObserver.observe(nextPill);
        }
      };

      const sync = () => {
        const desktop = window.innerWidth > 749;
        const checkout = findCheckout();
        const logicallyActive = !desktop
          && hasPopulatedCart()
          && checkout
          && checkout.getBoundingClientRect().bottom <= 0;
        if (!logicallyActive) {
          setPillState(false);
          bindPillObserver();
          return;
        }

        // Reveal first so the active-state CSS can place the pill at the
        // viewport edge before its geometry is used to gate motion controls.
        pill.hidden = false;
        pill.setAttribute('aria-hidden', 'false');
        pill.dataset.cartPillIntersecting = 'false';
        if (container.dataset) container.dataset.cartPillVisible = 'false';
        if (isInViewport(pill)) setPillState(true);
        else setPillState(false);
        bindPillObserver();
      };
      const schedule = () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          bindCheckoutObserver();
          sync();
        });
      };
      bindCheckoutObserver();
      if ('MutationObserver' in window) {
        mutationObserver = new MutationObserver((mutations) => {
          const currentPill = rootsFor(container, '[data-cart-mobile-pill]')[0] || null;
          if (currentPill !== pill) {
            pillControllers.get(pill)?.disconnect();
            initCartPills(container);
            return;
          }
          if (mutations.length > 0 && mutations.every(({ target }) => target === pill)) return;
          schedule();
        });
        mutationObserver.observe(container, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'disabled', 'hidden', 'aria-disabled'],
        });
      }
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      pill.dataset.bound = 'true';
      sync();
      pillControllers.set(pill, {
        disconnect() {
          checkoutIntersectionObserver?.disconnect();
          pillIntersectionObserver?.disconnect();
          mutationObserver?.disconnect();
          window.removeEventListener('scroll', schedule);
          window.removeEventListener('resize', schedule);
          if (frame) cancelAnimationFrame(frame);
          delete pill.dataset.bound;
          if (container.dataset) delete container.dataset.cartPillVisible;
          pillControllers.delete(pill);
        },
      });
    });
  }

  function initAll(scope = document) {
    // A server-rendered Remove must never look usable when this runtime failed
    // to load. Re-enable only complete native-priced groups with active config.
    rootsFor(scope, '[data-bundle-roster][data-components-priced="true"]').forEach((roster) => {
      // Only a roster attached to a real cart line can be acted on: the member
      // queue resolves its parent key from that row. The post-add recap clones
      // the roster into the visual confirmation, so that copy must stay inert.
      if (!roster.closest('.cart-item[data-line-key]')) return;
      if (!parseAovConfig(roster.querySelector('[data-cart-member-config]')?.textContent)) return;
      roster.querySelectorAll('[data-cart-member-remove]').forEach((button) => { button.hidden = false; });
      roster.querySelectorAll('[data-cart-bundle-remove]').forEach((button) => { button.hidden = false; });
      roster.dataset.memberRuntime = 'ready';
    });
    rootsFor(scope, '[data-miniami-parallel-bundle]').forEach(initBundle);
    initUpsellForms(scope);
    initCartPills(scope);
    if (memberOperationPending) paintMemberPending();
  }

  function cleanup(scope = document) {
    rootsFor(scope, '[data-miniami-parallel-bundle]').forEach((root) => bundleControllers.get(root)?.disconnect());
    rootsFor(scope, cartRootSelector).forEach((root) => upsellControllers.get(root)?.disconnect());
    rootsFor(scope, '[data-cart-mobile-pill]').forEach((pill) => pillControllers.get(pill)?.disconnect());
  }

  window.MiniamiParallelCommerce = Object.freeze({
    escapeHtml,
    formatMoney,
    parseTiers,
    tierFor,
    parseAovConfig,
    planAovBundle,
    validateNativeBundleRoster,
    bundleSelectionIds,
    resolveBundleContinuation,
    updateBundleContinuation,
    verifyNativeBundle,
    planBundleMemberRemoval,
    updateBundleDissolution,
    cartMutationPending: () => memberOperationPending || memberDisplayUnconfirmed || commercePending > 0,
    enqueueMutation: enqueueCommerce,
    selectionFingerprint: (items) => [...items]
      .sort((left, right) => left.id - right.id)
      .map((item) => `${item.id}:${item.properties?.bundle || ''}`)
      .join('|'),
  });

  // cart-drawer.renderContents() replaces the drawer's own markup. Re-run the
  // idempotent gate on the swapped surface so member and whole-bundle controls
  // are revealed exactly as on a static load, without a navigation.
  document.addEventListener('miniami:cart-drawer-rendered', (event) => {
    initAll(event.target?.matches?.('cart-drawer') ? event.target : document);
  });
  document.addEventListener('shopify:section:load', (event) => initAll(event.target || document));
  document.addEventListener('shopify:section:unload', (event) => cleanup(event.target || document));
  document.addEventListener('shopify:section:reorder', (event) => initAll(event.target || document));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
})();
